/**
 * The Pro Hub's one door to Tenean's API on the cell (move map M28). Every screen action that read Supabase calls an
 * endpoint here instead — one endpoint per action (tenean-infra api/openapi.yaml, the Pro Hub section). Answers come
 * back in supabase-js's own shape, { data, error }, so each screen's existing error handling is unchanged.
 *
 * View-as stays read only on this path too: any write while viewing resolves to the same refusal supabase.ts gives.
 */
import { API_BASE } from './config';
import { getCellSession } from './cognito';
import { viewAsProId } from '../viewAs';

export interface CellError { message: string; code?: string; status: number }
export type CellResult<T> = { data: T | null; error: CellError | null };

const RO_MSG = 'View-as is read only — nothing was changed.';

export async function cellCall<T>(method: 'GET' | 'POST' | 'PATCH' | 'DELETE', path: string, opts: { query?: Record<string, string | null | undefined>; body?: unknown; readInViewAs?: boolean } = {}): Promise<CellResult<T>> {
  if (method !== 'GET' && viewAsProId() && !opts.readInViewAs) return { data: null, error: { message: RO_MSG, status: 0 } };
  const s = await getCellSession();
  if (!s) return { data: null, error: { message: 'Not signed in', status: 401 } };
  const q = new URLSearchParams();
  for (const [k, v] of Object.entries(opts.query ?? {})) if (v !== undefined && v !== null && v !== '') q.set(k, v);
  const url = `${API_BASE}${path}${q.toString() ? `?${q}` : ''}`;
  try {
    const r = await fetch(url, {
      method,
      headers: { authorization: `Bearer ${s.idToken}`, ...(opts.body !== undefined ? { 'content-type': 'application/json' } : {}) },
      body: opts.body !== undefined ? JSON.stringify(opts.body) : undefined,
    });
    const j = (await r.json().catch(() => null)) as (T & { error?: string; message?: string; code?: string }) | null;
    if (!r.ok) return { data: null, error: { message: j?.message ?? j?.error ?? `HTTP ${r.status}`, code: j?.code ?? j?.error, status: r.status } };
    return { data: j as T, error: null };
  } catch (e) {
    return { data: null, error: { message: (e as Error).message || 'Network error', status: 0 } };
  }
}

export const cellGet = <T>(path: string, query?: Record<string, string | null | undefined>) => cellCall<T>('GET', path, { query });
export const cellPost = <T>(path: string, body?: unknown, opts: { readInViewAs?: boolean } = {}) => cellCall<T>('POST', path, { body: body ?? {}, ...opts });

/** The view-as query argument the API passes to p_view_pro (the cell path's viewRpcArgs()). */
export function viewQuery(): Record<string, string> {
  const id = viewAsProId();
  return id ? { viewPro: id } : {};
}
